import constants from '../configs/constants';
import reduxStore from '../utils/redux/store';

// The LLM proxy is mounted at the API host root, not under /api — same base the
// LLM Playground's ChatModelAdapter uses.
const LLM_BASE_URL = constants.API_URL.replace('/api', '');

// Cheap + fast; matches the LLM Playground default so we know it is routable.
const ENHANCE_MODEL = 'gpt-4o-mini';

const SYSTEM_PROMPT_BY_KIND = {
  image: 'You rewrite short image-generation prompts into vivid, specific ones. Keep the user\'s subject and intent exactly. Add concrete detail about composition, lighting, lens/medium, colour and mood. Never invent brand names, text overlays or people who were not asked for. Reply with ONLY the rewritten prompt — no preamble, no quotes, no markdown, under 120 words.',
  video: 'You rewrite short video-generation prompts into vivid, specific ones. Keep the user\'s subject and intent exactly. Add concrete detail about camera movement, shot type, pacing, lighting and mood. Never invent brand names or dialogue that was not asked for. Reply with ONLY the rewritten prompt — no preamble, no quotes, no markdown, under 120 words.',
  text: 'You sharpen a short instruction into a clearer, more specific one while preserving the user\'s intent exactly. Reply with ONLY the rewritten instruction — no preamble, no quotes, no markdown, under 120 words.',
};

/**
 * Rewrite a prompt into a richer one using the URO LLM proxy.
 * @param {string} prompt          The user's current prompt.
 * @param {'image'|'video'|'text'} kind  What is being generated — picks the system prompt.
 * @returns {Promise<string>}      The rewritten prompt.
 * @throws  When the prompt is empty or the LLM call fails.
 */
export async function enhancePrompt(prompt, kind = 'image') {
  const trimmed = typeof prompt === 'string' ? prompt.trim() : '';
  if (!trimmed) throw new Error('Write something first, then Enhance will expand it.');

  const token = reduxStore.store.getState().auth.token;
  const res = await fetch(`${LLM_BASE_URL}/llm/v1/chat/completions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({
      model: ENHANCE_MODEL,
      messages: [
        { role: 'system', content: SYSTEM_PROMPT_BY_KIND[kind] || SYSTEM_PROMPT_BY_KIND.image },
        { role: 'user', content: trimmed },
      ],
      temperature: 0.8,
      max_tokens: 400,
      stream: false,
    }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.error?.message || `Enhance failed (${res.status})`);
  }

  const data = await res.json();
  const text = data?.choices?.[0]?.message?.content;
  if (typeof text !== 'string' || !text.trim()) throw new Error('Enhance returned nothing');
  // Models occasionally wrap the answer in quotes despite the instruction.
  return text.trim().replace(/^["'`]+|["'`]+$/g, '').trim();
}

const promptEnhanceActions = { enhancePrompt };
export default promptEnhanceActions;
